import constantURL from "./ConstantURL";
import { getDataAPI } from "./index";
import { getStoredUserToken } from "../utils/sessionContext";
import { normalizeSendOtpMeta } from "./familyportalOtpApi";

export const PROFILE_ROLE_FAMILY_MEMBER = "FAMILYMEMBER";
export const PROFILE_ROLE_CAREGIVER = "CAREGIVER";

const normalizeEditMobileNumber = (mobileNumber) => {
  const trimmed = String(mobileNumber || "").trim();
  if (!trimmed) {
    return "";
  }

  const hasPlusPrefix = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) {
    return trimmed;
  }

  return hasPlusPrefix ? `+${digits}` : digits;
};

const postAuthJson = async (url, payload) => {
  try {
    const response = await fetch(url, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getStoredUserToken() || ""}`,
      },
      body: JSON.stringify(payload),
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!data) {
      return { success: false, message: "Unexpected server response.", data: null };
    }

    if (data.status === "success") {
      return {
        success: true,
        message: data.message || "Success.",
        data: data.data ?? null,
      };
    }

    return {
      success: false,
      message: data.message || "Request failed.",
      data: data.data ?? null,
    };
  } catch {
    return { success: false, message: "Unable to reach the server. Please try again.", data: null };
  }
};

export const getProfileDetails = async ({ emailId, role }) => {
  const normalizedEmail = String(emailId || "").trim();
  const normalizedRole = String(role || "")
    .trim()
    .toUpperCase();

  if (!normalizedEmail) {
    return { success: false, message: "Email is required.", data: null };
  }

  if (!normalizedRole) {
    return { success: false, message: "Role is required.", data: null };
  }

  try {
    const query = new URLSearchParams({
      emailId: normalizedEmail,
      role: normalizedRole,
    });
    const response = await getDataAPI(`${constantURL.authGetProfileDetails}?${query.toString()}`);

    if (!response) {
      return { success: false, message: "Unexpected server response.", data: null };
    }

    if (response.status === "success" && response.data) {
      return {
        success: true,
        message: response.message || "Profile details fetched successfully.",
        data: response.data,
      };
    }

    return {
      success: false,
      message: response.message || "Failed to fetch profile details.",
      data: null,
    };
  } catch {
    return { success: false, message: "Unable to reach the server. Please try again.", data: null };
  }
};

export const getVerificationStatus = async ({ emailId, role }) => {
  const normalizedEmail = String(emailId || "").trim();
  const normalizedRole = String(role || "")
    .trim()
    .toUpperCase();

  if (!normalizedEmail) {
    return { success: false, message: "Email is required.", data: null };
  }

  if (!normalizedRole) {
    return { success: false, message: "Role is required.", data: null };
  }

  try {
    const query = new URLSearchParams({
      emailId: normalizedEmail,
      role: normalizedRole,
    });
    const response = await getDataAPI(`${constantURL.authGetVerificationStatus}?${query.toString()}`);

    if (!response) {
      return { success: false, message: "Unexpected server response.", data: null };
    }

    if (response.status === "success" && response.data) {
      return {
        success: true,
        message: response.message || "Verification status fetched successfully.",
        data: {
          isEmailVerified: Boolean(response.data.isEmailVerified),
          isMobileVerified: Boolean(response.data.isMobileVerified),
        },
      };
    }

    return {
      success: false,
      message: response.message || "Failed to fetch verification status.",
      data: null,
    };
  } catch {
    return { success: false, message: "Unable to reach the server. Please try again.", data: null };
  }
};

export const sendEditMobileOtp = async ({ emailId, role, mobileNumber }) => {
  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();
  const normalizedRole = String(role || "")
    .trim()
    .toUpperCase();
  const normalizedMobile = normalizeEditMobileNumber(mobileNumber);

  if (!normalizedEmail) {
    return { success: false, message: "Email is required.", data: null };
  }

  if (!normalizedRole) {
    return { success: false, message: "Role is required.", data: null };
  }

  if (!normalizedMobile) {
    return { success: false, message: "Please enter a mobile number.", data: null };
  }

  const result = await postAuthJson(constantURL.authSendEditMobileOtp, {
    emailId: normalizedEmail,
    role: normalizedRole,
    mobileNumber: normalizedMobile,
  });

  if (!result.data) {
    return result;
  }

  return {
    ...result,
    data: normalizeSendOtpMeta(result.data),
  };
};

export const validateEditMobileOtp = async ({ emailId, role, mobileNumber, otpCode }) => {
  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();
  const normalizedRole = String(role || "")
    .trim()
    .toUpperCase();
  const normalizedMobile = normalizeEditMobileNumber(mobileNumber);
  const code = String(otpCode || "").replace(/\D/g, "");

  if (!normalizedEmail) {
    return { success: false, message: "Email is required.", data: null };
  }

  if (!normalizedRole) {
    return { success: false, message: "Role is required.", data: null };
  }

  if (!normalizedMobile) {
    return { success: false, message: "Please enter a mobile number.", data: null };
  }

  if (!code) {
    return { success: false, message: "Please enter the verification code.", data: null };
  }

  return postAuthJson(constantURL.authValidateEditMobileOtp, {
    emailId: normalizedEmail,
    role: normalizedRole,
    mobileNumber: normalizedMobile,
    otpCode: code,
  });
};

export const updateMobileNumber = async ({ emailId, role, mobileNumber }) => {
  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();
  const normalizedRole = String(role || "")
    .trim()
    .toUpperCase();
  const normalizedMobile = normalizeEditMobileNumber(mobileNumber);

  if (!normalizedEmail) {
    return { success: false, message: "Email is required.", data: null };
  }

  if (!normalizedRole) {
    return { success: false, message: "Role is required.", data: null };
  }

  if (!normalizedMobile) {
    return { success: false, message: "Please enter a valid mobile number.", data: null };
  }

  return postAuthJson(constantURL.authUpdateMobileNumber, {
    emailId: normalizedEmail,
    role: normalizedRole,
    mobileNumber: normalizedMobile,
  });
};
